/**
 * Utilities for generating structured data for SEO
 */
import { ProfileData } from '@/hooks/useProfiles';

/**
 * Generates structured data for a profile in Schema.org format
 * @param profile The profile data to generate structured data for
 * @returns Structured data object or null if profile is invalid
 */
export const generateProfileStructuredData = (profile: ProfileData | null): Record<string, any> | null => {
  if (!profile) return null;

  const profession = profile.derivedProfession || profile.profession || 'Designer';
  const location = profile.location || 'Africa';
  
  // Basic person schema
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": profile.username,
    "description": profile.bio || `${profile.username} is a top ${profession} from ${location}`,
    "jobTitle": profession,
    "address": {
      "@type": "PostalAddress",
      "addressCountry": location
    }
  };

  // Add social profiles if available
  if (profile.instagram_username || profile.twitter_username || profile.linkedin_username) {
    personSchema["sameAs"] = [];
    
    if (profile.instagram_username) {
      personSchema["sameAs"].push(`https://www.instagram.com/${profile.instagram_username}`);
    }
    
    if (profile.twitter_username) {
      personSchema["sameAs"].push(`https://twitter.com/${profile.twitter_username}`);
    }
    
    if (profile.linkedin_username) {
      personSchema["sameAs"].push(`https://www.linkedin.com/in/${profile.linkedin_username}`);
    }
  }
  
  // Add image if available
  if (profile.avatar_url) {
    personSchema["image"] = profile.avatar_url;
  }
  
  // Add portfolio works as CreativeWorks if available
  if (profile.portfolio_items && profile.portfolio_items.length > 0) {
    personSchema["mainEntityOfPage"] = {
      "@type": "ProfilePage",
      "@id": `${window.location.origin}/u/${profile.username_slug || profile.id}`
    };
    
    // Add works
    personSchema["knowsAbout"] = profile.skills || [];
    
    // Add portfolio items as creative works, filtering out drafts
    personSchema["performerIn"] = profile.portfolio_items
      .filter(item => item.status !== "draft")
      .map(item => ({
        "@type": "CreativeWork",
        "name": item.title || "Portfolio Project",
        "description": item.description || "",
        "image": item.image_url || ""
      }));
  }
  
  return personSchema;
};

/**
 * Derives a profession label from skills when profession field is empty
 * @param profile Profile data
 * @returns Derived profession string or null
 */
export const getProfessionFromSkills = (profile: ProfileData): string | null => {
  if (profile.profession) return profile.profession;
  
  // If profession is null but skills exist, use the first skill
  if (profile.skills && profile.skills.length > 0) {
    const skill = profile.skills[0];
    // Convert kebab-case to Title Case (e.g., "graphic-designer" to "Graphic Designer")
    return skill
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  
  return null; // Let child components handle the default fallback
};
